import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';

// Add custom animations for dialog
const style = document.createElement('style');
style.textContent = `
  @keyframes dialog-content-below {
    from { opacity: 0; transform: translateY(-10px) translateX(-50%); }
    to { opacity: 1; transform: translateY(0) translateX(-50%); }
  }
  @keyframes dialog-content-below-no-transform {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes dialog-top-left-quarter {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  [dir="rtl"] .DialogContent { direction: rtl; text-align: right; }
`;
document.head.appendChild(style);

const rootEl = document.getElementById("root")!;

// Check env vars BEFORE importing anything that touches Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabaseKey) {
  rootEl.innerHTML = `
    <div dir="rtl" style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;background:#fafafa;padding:2rem;">
      <div style="max-width:400px;text-align:center;">
        <h1 style="font-size:1.5rem;margin-bottom:1rem;color:#c00;">שגיאת תצורה</h1>
        <p style="color:#555;margin-bottom:1.5rem;line-height:1.6;">
          האפליקציה לא הצליחה לטעון בגלל חוסר בהגדרות שרת.<br/>
          אנא נסו לרענן את הדף.
        </p>
        <button onclick="location.reload()" style="padding:10px 24px;border-radius:8px;border:none;background:#6366f1;color:#fff;font-size:1rem;cursor:pointer;">
          רענון הדף
        </button>
      </div>
    </div>
  `;
  console.error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY');
} else {
  // Safe to import the app — Supabase client will initialize successfully.
  // Kick off the Home page chunk download alongside App so it's already in the
  // browser cache by the time React mounts and ProtectedRoute lets it render.
  // The third import's result is intentionally unused; it's a fire-and-forget
  // chunk-warmer.
  Promise.all([
    import('./App'),
    import('@/services/eventLogService'),
    import('@/pages/Home'),
    import('@/integrations/posthog/client'),
  ]).then(([{ default: App }, { logEvent }, _home, { initPostHog }]) => {
    initPostHog();
    // Android install referrer → PostHog super-properties (install_source).
    void import('@/services/installSource').then((m) => m.getInstallSource());
    // Register global error handlers
    window.addEventListener('error', (event) => {
      try {
        logEvent('error', 'crash', 'unhandled_error', {
          message: event.message,
          filename: event.filename,
          line: event.lineno,
          col: event.colno,
        });
      } catch { /* silent */ }
    });

    window.addEventListener('unhandledrejection', (event) => {
      try {
        const reason = event.reason instanceof Error
          ? { message: event.reason.message, stack: event.reason.stack?.substring(0, 2000) }
          : { message: String(event.reason) };
        // Transient network blips (mobile WebView wakes from background before connectivity is
        // ready) surface as uncaught "Failed to fetch". App.tsx already preventDefault()s every
        // rejection so they don't crash anything, and nothing awaited them. Downgrade to a non-crash
        // breadcrumb so they don't flood crash/error tracking — but keep them countable (still in
        // app_event_logs as info) so a real outage spike is still visible.
        const msg = (reason.message || '');
        const isTransientNetwork = /Failed to fetch|Load failed|NetworkError|network ?error/i.test(msg);
        if (isTransientNetwork) {
          logEvent('info', 'performance', 'network_transient', reason);
        } else {
          logEvent('error', 'crash', 'unhandled_rejection', reason);
        }
      } catch { /* silent */ }
    });

    createRoot(rootEl).render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  }).catch((err) => {
    console.error('App bootstrap failed:', err);
    rootEl.innerHTML = `
      <div dir="rtl" style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;background:#fafafa;padding:2rem;">
        <div style="max-width:400px;text-align:center;">
          <h1 style="font-size:1.5rem;margin-bottom:1rem;color:#c00;">שגיאה בטעינה</h1>
          <p style="color:#555;margin-bottom:1.5rem;">אירעה שגיאה בטעינת האפליקציה. אנא נסו לרענן.</p>
          <button onclick="location.reload()" style="padding:10px 24px;border-radius:8px;border:none;background:#6366f1;color:#fff;font-size:1rem;cursor:pointer;">רענון</button>
        </div>
      </div>
    `;
  });
}
